import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./case_study.css";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import ApiRequest from "../../helper/ApiRequest";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { BaseUrl } from "../../helper/BaseUrl";
import { toast } from "react-toastify";

const caseEditEndpoint = ApiEndpoints.newCase.adminCaseEdit;
const caseUpdateEndpoint = ApiEndpoints.newCase.adminCaseUpdate;

export default function EditCaseStudy() {
  const { casestudy_id } = useParams();

  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [preview, setPreview] = useState({
    logo: null,
    thumbnail: null,
    banner: null,
    mobility_feature_previews: {},
    tech_stack_icon_previews: {},
  });

  const [postData, setPostData] = useState({
    title: "",
    slug: "",
    casestudycategory_id: "",
    logo: null,
    alt_logo: "",
    brief: "",
    app_rating: "",
    app_downloads: "",
    app_operational_efficiency: "",
    app_operational_efficiency_heading:"",
    googleplay: "",
    appstore: "",
    thumbnail: null,
    alt_thumbnail: "",
    listing_card_color: "",
    banner: null,
    alt_banner: "",
    heading: "",
    description: "",
    founded_year: "",
    primary_location: "",
    project_challenges_description: "",
    quote: "",
    quote2: "",
    rank: "",
    mobility_solution_description: "",
    project_challenges: [{ heading: "", description: "" }],
    mobility_solution_features: [
      { feature_name: "", image: null, alt_image: "", details: [{ heading: "", description: "" }] },
    ],
    impact_in_numbers: [{ heading: "", description: "" }],
    tech_stack: [{ tech_stack_category: "", tech_stack_technologies: [{ name: "", tech_stack_icon: "", alt_tech_stack_icon: "" }] }]
  });

  useEffect(() => {

    const fetchEditData = async () => {
      try {
        const response = await ApiRequest("GET", `${caseEditEndpoint}${casestudy_id}`);
        setPostData(response?.newcasestudy || []);
        setCategories(response?.categories || [])

        // image preview setting
        setPreview({
          logo: response?.newcasestudy.logo
            ? `${BaseUrl}${response.newcasestudy.logo}`
            : null,
          thumbnail: response?.newcasestudy.thumbnail
            ? `${BaseUrl}${response.newcasestudy.thumbnail}`
            : null,
          banner: response?.newcasestudy.banner
            ? `${BaseUrl}${response.newcasestudy.banner}`
            : null,
          mobility_feature_previews: Array?.isArray(response?.newcasestudy?.mobility_solution_features)
            ? response.newcasestudy.mobility_solution_features.reduce((acc, item, index) => {
              acc[index] = item.image ? `${BaseUrl}${item.image}` : null;
              return acc;
            }, {})
            : {},
          tech_stack_icon_previews: Array.isArray(response?.newcasestudy?.tech_stack)
            ? response.newcasestudy.tech_stack.reduce((acc, cat, ci) => {
              if (Array.isArray(cat.tech_stack_technologies)) {
                cat.tech_stack_technologies.forEach((tech, ti) => {
                  acc[`${ci}-${ti}`] = tech.tech_stack_icon
                    ? `${BaseUrl}${tech.tech_stack_icon}`
                    : null;
                });
              }
              return acc;
            }, {})
            : {},
        });

      } catch (error) {
        console.error("Failed to fetch category:", error);
        setCategories([]);
      }
    }

    fetchEditData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    if (type === "file") return;
    setPostData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (!files || files.length === 0) return;
    const file = files[0];
    setPostData((prev) => ({ ...prev, [name]: file }));

    const reader = new FileReader();
    reader.onload = () => {
      if (["thumbnail", "logo", "banner"].includes(name)) {
        setPreview((prev) => ({ ...prev, [name]: reader.result }));
      }
    };
    reader.readAsDataURL(file);
  };


  //============================= Project Challenges handlers Start ====================== ========================================================================================
  const addProjectChallenge = () => {
    setPostData((prev) => ({
      ...prev,
      project_challenges: [...prev.project_challenges, { heading: "", description: "" }],
    }));
  };

  const removeProjectChallenge = (index) => {
    setPostData((prev) => {
      const list = [...prev.project_challenges];
      list.splice(index, 1);
      return { ...prev, project_challenges: list.length ? list : [{ heading: "", description: "" }] };
    });
  };

  const updateProjectChallenge = (index, field, value) => {
    setPostData((prev) => {
      const list = prev.project_challenges?.map((item, i) => (i === index ? { ...item, [field]: value } : item));
      return { ...prev, project_challenges: list };
    });
  };
  //=========================== Project Challenges handlers End ========================== ========================================================================================


  //=========================== Impact Section handlers Start ============================ ========================================================================================
  const addImpact = () => {
    setPostData((prev) => ({ ...prev, impact_in_numbers: [...prev.impact_in_numbers, { heading: "", description: "" }] }));
  };

  const removeImpact = (index) => {
    setPostData((prev) => {
      const list = [...prev.impact_in_numbers];
      list.splice(index, 1);
      return { ...prev, impact_in_numbers: list.length ? list : [{ heading: "", description: "" }] };
    });
  };

  const updateImpact = (index, field, value) => {
    setPostData((prev) => {
      const list = prev.impact_in_numbers?.map((item, i) => (i === index ? { ...item, [field]: value } : item));
      return { ...prev, impact_in_numbers: list };
    });
  };
  //========================== Impact Section handlers End =============================== ========================================================================================


  //========================= Solution Section handlers Start ============================ ========================================================================================
  const addMobilityFeature = () => {
    setPostData((prev) => ({
      ...prev,
      mobility_solution_features: [
        ...prev.mobility_solution_features,
        { feature_name: "", image: null, alt_image: "", details: [{ heading: "", description: "" }] },
      ],
    }));
  };

  const removeMobilityFeature = (index) => {
    setPostData((prev) => {
      const list = [...prev.mobility_solution_features];
      list.splice(index, 1);
      setPreview((p) => {
        const copy = { ...p };
        if (copy.mobility_feature_previews) {
          const newmap = { ...copy.mobility_feature_previews };
          delete newmap[index];
          copy.mobility_feature_previews = newmap;
        }
        return copy;
      });
      return { ...prev, mobility_solution_features: list.length ? list : [{ feature_name: "", image: null, alt_image: "", details: [{ heading: "", description: "" }] }] };
    });
  };

  const updateMobilityFeature = (index, field, value) => {
    setPostData((prev) => {
      const list = prev.mobility_solution_features?.map((item, i) => (i === index ? { ...item, [field]: value } : item));
      return { ...prev, mobility_solution_features: list };
    });
  };

  const handleMobilityFeatureImage = (index, file) => {
    setPostData((prev) => {
      const list = prev.mobility_solution_features?.map((item, i) => (i === index ? { ...item, image: file } : item));
      return { ...prev, mobility_solution_features: list };
    });

    const reader = new FileReader();
    reader.onload = () => {
      setPreview((prev) => ({
        ...prev,
        mobility_feature_previews: { ...(prev.mobility_feature_previews || {}), [index]: reader.result },
      }));
    };
    reader.readAsDataURL(file);
  };

  // Mobility detail handlers
  const addMobilityDetail = (featureIndex) => {
    setPostData((prev) => {
      const list = prev.mobility_solution_features?.map((f, i) =>
        i === featureIndex ? { ...f, details: [...f.details, { heading: "", description: "" }] } : f
      );
      return { ...prev, mobility_solution_features: list };
    });
  };

  const removeMobilityDetail = (featureIndex, detailIndex) => {
    setPostData((prev) => {
      const list = prev.mobility_solution_features?.map((f, i) => {
        if (i !== featureIndex) return f;
        const newDetails = [...f.details];
        newDetails.splice(detailIndex, 1);
        return { ...f, details: newDetails.length ? newDetails : [{ heading: "", description: "" }] };
      });
      return { ...prev, mobility_solution_features: list };
    });
  };

  const updateMobilityDetail = (featureIndex, detailIndex, field, value) => {
    setPostData((prev) => {
      const list = prev.mobility_solution_features?.map((f, i) => {
        if (i !== featureIndex) return f;
        const details = f.details?.map((d, di) => (di === detailIndex ? { ...d, [field]: value } : d));
        return { ...f, details };
      });
      return { ...prev, mobility_solution_features: list };
    });
  };

  //================================ Solution Section handlers End ======================= ========================================================================================

  //================================ Tech Stack handlers Start ======================= ========================================================================================
  const updateTechStackCategory = (ci, value) => {
    const updated = [...postData.tech_stack];
    updated[ci].tech_stack_category = value;
    setPostData({ ...postData, tech_stack: updated });
  };

  const updateTechnology = (ci, ti, field, value) => {
    const updated = [...postData.tech_stack];
    updated[ci].tech_stack_technologies[ti][field] = value;
    setPostData({ ...postData, tech_stack: updated });
  };

  const handleTechIcon = (ci, ti, file) => {
    const updated = [...postData.tech_stack];
    updated[ci].tech_stack_technologies[ti].tech_stack_icon = file;
    setPostData({ ...postData, tech_stack: updated });

    const previewURL = URL.createObjectURL(file);
    setPreview(prev => ({
      ...prev,
      tech_stack_icon_previews: {
        ...prev.tech_stack_icon_previews,
        [`${ci}-${ti}`]: previewURL
      }
    }));
  };

  const addTechnology = (ci) => {
    const updated = [...postData.tech_stack];
    updated[ci].tech_stack_technologies.push({
      name: "",
      tech_stack_icon: "",
      alt_tech_stack_icon: ""
    });
    setPostData({ ...postData, tech_stack: updated });
  };

  const removeTechnology = (ci, ti) => {
    const updated = [...postData.tech_stack];
    updated[ci].tech_stack_technologies.splice(ti, 1);
    setPostData({ ...postData, tech_stack: updated });
  };

  const addTechCategory = () => {
    setPostData({
      ...postData,
      tech_stack: [
        ...postData.tech_stack,
        {
          tech_stack_category: "",
          tech_stack_technologies: [
            { name: "", tech_stack_icon: "", alt_tech_stack_icon: "" }
          ]
        }
      ]
    });
  };

  const removeTechCategory = (ci) => {
    const updated = [...postData.tech_stack];
    updated.splice(ci, 1);
    setPostData({ ...postData, tech_stack: updated });
  };

  //================================ Tech Stack handlers End ======================= ========================================================================================


  const handleEditorChange = (name, data) => {
    setPostData((prevData) => ({ ...prevData, [name]: data }));
  };

  //================================ Validation Function Start =========================== ========================================================================================

  const OPTIONAL_FIELDS = [
    "googleplay",
    "appstore",
    "banner",
    "logo",
    "thumbnail",
    "mobility_solution_features.image", //nested
  ];

  const validatePostData = (data, parentKey = "") => {
    for (const key in data) {
      const value = data[key];
      const fieldPath = parentKey ? `${parentKey}.${key}` : key;

      // Skip optional fields
      if (
        OPTIONAL_FIELDS.includes(fieldPath) ||
        OPTIONAL_FIELDS.some(opt => fieldPath.startsWith(opt + "["))
      ) {
        continue;
      }

      // Arrays
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return `${fieldPath} cannot be empty`;
        }

        for (let i = 0; i < value.length; i++) {
          const error = validatePostData(value[i], `${fieldPath}[${i}]`);
          if (error) return error;
        }
      }

      // Objects
      else if (typeof value === "object" && value !== null) {
        const hasEmpty = Object.values(value).some(v => {
          if (typeof v === "string") return v.trim() === "";
          return v === null || v === undefined;
        });

        if (hasEmpty) {
          return `${fieldPath} cannot be empty`;
        }
      }

      // Primitive values
      else if (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "")
      ) {
        return `${fieldPath} cannot be empty`;
      }
    }

    return null;
  };

  //================================ Validation Function End ===================== ================================================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    const error = validatePostData(postData);
    if (error) {
      toast.error(error);
      return;
    }

    try {

      const formData = new FormData();

      Object.entries(postData).forEach(([key, value]) => {
        if (key === "project_challenges" || key === "impact_in_numbers") {
          formData.append(key, JSON.stringify(value));
        }
        else if (key === "mobility_solution_features") {
          const msf = postData.mobility_solution_features || [];

          // Build JSON where `image` remains the filename (if a File, use file.name)
          const msfJson = msf?.map((item) => ({
            feature_name: item.feature_name,
            image: item.image instanceof File ? item.image.name : item.image || null,
            alt_image: item.alt_image,
            details: item.details || [],
          }));

          // keep a JSON copy for the nested array field
          formData.append("mobility_solution_features", JSON.stringify(msfJson));

          msf.forEach((item, idx) => {
            if (item.image instanceof File) {
              // nested key pattern: mobility_solution_features[<index>][image]
              formData.append(`mobility_solution_features[${idx}][image]`, item.image, item.image.name);
            }
          });
        }

        else if (key === "tech_stack") {
          const techStack = postData.tech_stack || [];

          // 1️⃣ JSON STRUCTURE (icons as string names)
          const techStackJson = techStack.map((cat) => ({
            tech_stack_category: cat.tech_stack_category,
            tech_stack_technologies: cat.tech_stack_technologies.map((tech) => ({
              name: tech.name,
              tech_stack_icon:
                tech.tech_stack_icon instanceof File
                  ? tech.tech_stack_icon.name
                  : tech.tech_stack_icon || null,
              alt_tech_stack_icon: tech.alt_tech_stack_icon,
            })),
          }));

          // append JSON
          formData.append("tech_stack", JSON.stringify(techStackJson));

          // 2️⃣ FILE APPEND (only new uploads)
          techStack.forEach((cat, ci) => {
            cat.tech_stack_technologies.forEach((tech, ti) => {
              if (tech.tech_stack_icon instanceof File) {
                formData.append(
                  `tech_stack[${ci}][tech_stack_technologies][${ti}][tech_stack_icon]`,
                  tech.tech_stack_icon,
                  tech.tech_stack_icon.name
                );
              }
            });
          });
        }

        else {
          formData.append(key, value);
        }
      });

      const response = await ApiRequest("POST", caseUpdateEndpoint + casestudy_id, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.error === true) {
        toast.error(response.message);
      } else {
        toast.success(response.message)
        navigate("/admin/case-study");
      }

    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit. Check console for details.");
    }
  };

  return (
    <>
      <div className="header-add-category">
        <h5>Edit Case Study</h5>
      </div>
      <div className="add-casestudy-content">
        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* Title */}
            <div className="col-lg-12">
              <div className="section-wrapper">
                <div className="row">
                  <div className="col-md-6">
                    <label>Title:*</label>
                    <input name="title" type="text" className="form-control" value={postData.title} onChange={handleChange} />
                  </div>

                  {/* slug */}
                  <div className="col-md-6">
                    <label>Slug:*</label>
                    <input name="slug" type="text" className="form-control" value={postData.slug} onChange={handleChange} />
                  </div>

                  <div className="col-md-6">
                    <label>Rank:*</label>
                    <input name="rank" type="number" className="form-control" value={postData.rank} onChange={handleChange} />
                  </div>

                  {/* category */}
                  <div className="col-md-6">
                    <label>Category:*</label>
                    <select name="casestudycategory_id" className="form-control" value={postData.casestudycategory_id} onChange={handleChange}>
                      <option value="">Select Category</option>
                      {categories.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label>quote:*</label>
                    <input name="quote" type="text" className="form-control" value={postData.quote} onChange={handleChange} />
                  </div>

                  <div className="col-md-6">
                    <label>second quote:*</label>
                    <input name="quote2" type="text" className="form-control" value={postData.quote2} onChange={handleChange} />
                  </div>

                  {/* logo file */}
                  <div className="col-md-6">
                    <label>Logo:*</label>
                    <input name="logo" type="file" className="form-control" onChange={handleFileChange} />
                    {preview?.logo && <img src={preview.logo} alt="logo-preview" style={{ width: 80, marginTop: 6 }} />}
                  </div>

                  <div className="col-md-6">
                    <label>Alt Logo:*</label>
                    <input name="alt_logo" type="text" className="form-control" value={postData.alt_logo} onChange={handleChange} />
                  </div>

                  {/* banner file */}
                  <div className="col-md-6">
                    <label>Banner Image: (804 X 840 px)*</label>
                    <input name="banner" type="file" className="form-control" onChange={handleFileChange} />
                    {preview?.banner && <img src={preview.banner} alt="logo-preview" style={{ width: 80, marginTop: 6 }} />}
                  </div>

                  <div className="col-md-6">
                    <label>Alt Banner Image:*</label>
                    <input name="alt_banner" type="text" className="form-control" value={postData.alt_banner} onChange={handleChange} />
                  </div>

                  <div className="col-md-6">
                    <label>Logo Area Heading:*</label>
                    <input name="heading" type="text" className="form-control" value={postData.heading} onChange={handleChange} />
                  </div>

                  <div className="col-md-6">
                    <label>Logo Area description:*</label>
                    <input name="description" type="text" className="form-control" value={postData.description} onChange={handleChange} />
                  </div>

                  <div className="col-md-6">
                    <label>founded year:*</label>
                    <input name="founded_year" type="text" className="form-control" value={postData.founded_year} onChange={handleChange} />
                  </div>

                  <div className="col-md-6">
                    <label>primary location:*</label>
                    <input name="primary_location" type="text" className="form-control" value={postData.primary_location} onChange={handleChange} />
                  </div>
                </div>
              </div>
            </div>

            {/* =========Listing Page Card=========== */}
            <div className="col-lg-12">
              <div className="section-wrapper">
                <h6>Listing Card Area</h6>
                <div className="row">
                  <div className="col-md-6">
                    <label>Short Description:*</label>
                    <input name="brief" type="text" className="form-control" value={postData.brief} onChange={handleChange} />
                  </div>

                  <div className="col-md-6">
                    <label>listing card color:*</label>
                    <input name="listing_card_color" type="text" className="form-control" value={postData.listing_card_color} onChange={handleChange} />
                  </div>

                  <div className="col-md-6">
                    <label>app rating:*</label>
                    <input name="app_rating" type="text" className="form-control" value={postData.app_rating} onChange={handleChange} />
                  </div>

                  <div className="col-md-6">
                    <label>app downloads:*</label>
                    <input name="app_downloads" type="text" className="form-control" value={postData.app_downloads} onChange={handleChange} />
                  </div>

                  <div className="col-md-6">
                    <label>Efficiency Gain:*</label>
                    <input name="app_operational_efficiency" type="text" className="form-control" value={postData.app_operational_efficiency} onChange={handleChange} />
                  </div>

                  <div className="col-md-6">
                    <label>Efficiency Gain Heading:*</label>
                    <input name="app_operational_efficiency_heading" type="text" className="form-control" value={postData.app_operational_efficiency_heading} onChange={handleChange} />
                  </div>

                  <div className="col-md-6">
                    <label>googleplay url:</label>
                    <input name="googleplay" type="text" className="form-control" value={postData.googleplay} onChange={handleChange} />
                  </div>

                  <div className="col-md-6">
                    <label>appstore url:</label>
                    <input name="appstore" type="text" className="form-control" value={postData.appstore} onChange={handleChange} />
                  </div>

                  {/* Thumbnail Image */}
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Thumbnail Image (416 x 484 px):*</label>
                      <input name="thumbnail" type="file" className="form-control" onChange={handleFileChange} />
                      {preview?.thumbnail && <img src={preview.thumbnail} alt="Preview" style={{ width: "100px", maxHeight: "300px", marginTop: 8 }} />}
                    </div>
                  </div>

                  {/* Alt for thumbnail */}
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Alt Thumbnail Image:*</label>
                      <input name="alt_thumbnail" type="text" className="form-control" value={postData.alt_thumbnail} onChange={handleChange} />
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* Project Challenges array*/}
            <div className="col-md-12 mt-4">
              <h4>Project Challenge Section</h4>

              {/* project_challenges_description (CKEditor) */}
              <div className="form-group section-wrapper">
                <label>project challenges description:*</label>
                <CKEditor
                  editor={ClassicEditor}
                  data={postData.project_challenges_description}
                  onChange={(event, editor) => handleEditorChange("project_challenges_description", editor.getData())}
                  config={{
                    toolbar: ["heading", "|", "bold", "italic", "link", "bulletedList", "numberedList", "blockQuote", "undo", "redo",],
                  }}
                />
              </div>

              {postData.project_challenges.map((pc, idx) => (
                <div className="section-wrapper" key={idx}>
                  <div className="row">
                    <div className="col-lg-6">
                      <label>Box {idx + 1} Heading:*</label>
                      <input className="form-control" value={pc.heading} onChange={(e) => updateProjectChallenge(idx, "heading", e.target.value)} />
                    </div>
                    <div className="col-lg-6">
                      <label>Box {idx + 1} Description:*</label>
                      <input className="form-control" value={pc.description} onChange={(e) => updateProjectChallenge(idx, "description", e.target.value)} />
                    </div>
                  </div>

                  <div style={{ marginTop: 10 }}>
                    {/* remove button only for items after first */}
                    {idx > 0 && <button type="button" className="btn btn-sm btn-danger" onClick={() => removeProjectChallenge(idx)}>Remove</button>}
                  </div>
                </div>
              ))}

              {/* SINGLE Add More button for the whole section */}
              <div style={{ marginBottom: 15, textAlign: "center" }}>
                <button type="button" className="btn btn-sm btn-success" onClick={addProjectChallenge}>+ Add More Challenge</button>
              </div>
            </div>

            {/* Mobility Solution Features*/}
            <div className="col-md-12 mt-4">
              <h4>Mobility Solution Section</h4>

              <div className="form-group section-wrapper">
                <label>mobility solution description:*</label>
                <CKEditor
                  editor={ClassicEditor}
                  data={postData.mobility_solution_description}
                  onChange={(event, editor) => handleEditorChange("mobility_solution_description", editor.getData())}
                  config={{
                    toolbar: ["heading", "|", "bold", "italic", "link", "bulletedList", "numberedList", "blockQuote", "undo", "redo",],
                  }}
                />
              </div>

              {postData.mobility_solution_features.map((f, fi) => (
                <div key={fi} className="section-wrapper">
                  <h5>Feature {fi + 1} Area</h5>
                  <label>Feature {fi + 1} Name:*</label>
                  <input className="form-control" value={f.feature_name} onChange={(e) => updateMobilityFeature(fi, "feature_name", e.target.value)} />

                  <div className="row">
                    <div className="col-lg-6">
                      <label style={{ marginTop: 6 }}>Feature {fi + 1} Image (804 X 894 px):* </label>
                      <input type="file" className="form-control" onChange={(e) => { if (e.target.files[0]) handleMobilityFeatureImage(fi, e.target.files[0]); }} />
                      {preview.mobility_feature_previews && preview.mobility_feature_previews[fi] && (
                        <img src={preview.mobility_feature_previews[fi]} alt={`feature-${fi}`} style={{ width: 100, marginTop: 6 }} />
                      )}
                    </div>

                    <div className="col-lg-6">
                      <label style={{ marginTop: 6 }}>Alt Feature {fi + 1} Image:*</label>
                      <input className="form-control" value={f.alt_image} onChange={(e) => updateMobilityFeature(fi, "alt_image", e.target.value)} />
                    </div>
                  </div>

                  <div>
                    <strong style={{ margin: "30px 0 20px", display: "block" }}>Feature {fi + 1} Boxes:*</strong>
                    {f.details.map((d, di) => (
                      <div key={di} className="section-wrapper">
                        <div className="row">
                          <div className="col-lg-6">
                            <label>Box {di + 1} Heading:*</label>
                            <input className="form-control" value={d.heading} onChange={(e) => updateMobilityDetail(fi, di, "heading", e.target.value)} />
                          </div>
                          <div className="col-lg-6">
                            <label>Box {di + 1} Description:*</label>
                            <input className="form-control" value={d.description} onChange={(e) => updateMobilityDetail(fi, di, "description", e.target.value)} />
                          </div>
                        </div>
                        <div style={{ marginTop: 10 }}>
                          {/* remove detail only if not the first */}
                          {di > 0 && <button type="button" className="btn btn-sm btn-danger" onClick={() => removeMobilityDetail(fi, di)}>Remove Box</button>}
                        </div>
                      </div>
                    ))}

                    {/* Single Add Detail button PER FEATURE (keeps nested section tidy) */}
                    {postData?.mobility_solution_features?.[fi]?.details?.length < 5 && <div style={{ marginTop: 20, textAlign: "center" }}>
                      <button type="button" className="btn btn-sm btn-success" onClick={() => addMobilityDetail(fi)}>+ Add Box</button>
                    </div>}
                  </div>

                  <div style={{ marginTop: 20, }}>
                    {/* remove feature only for non-first */}
                    {fi > 0 && <button type="button" className="btn btn-sm btn-danger" onClick={() => removeMobilityFeature(fi)}>Remove Feature</button>}
                  </div>
                </div>
              ))}

              {/* SINGLE Add Feature button for the whole features section */}
              <div style={{ marginBottom: 20, textAlign: "center" }}>
                <button type="button" className="btn btn-sm btn-success" onClick={addMobilityFeature}>+ Add New Feature</button>
              </div>
            </div>

            {/* Tech Stack Section */}
            <div className="col-md-12 mt-4">
              <h4>Tech Stack Section</h4>

              {postData.tech_stack.map((cat, ci) => (
                <div key={ci} className="section-wrapper">
                  <h5>Category {ci + 1}</h5>

                  <label>Tech Stack Category:*</label>
                  <input
                    className="form-control"
                    value={cat.tech_stack_category}
                    onChange={(e) => updateTechStackCategory(ci, e.target.value)}
                  />

                  <strong style={{ margin: "20px 0", display: "block" }}>
                    Technologies
                  </strong>

                  {cat.tech_stack_technologies.map((tech, ti) => (
                    <div key={ti} className="section-wrapper">
                      <div className="row">
                        <div className="col-lg-4">
                          <label>Technology Name:*</label>
                          <input
                            className="form-control"
                            value={tech.name}
                            onChange={(e) => updateTechnology(ci, ti, "name", e.target.value)}
                          />
                        </div>

                        <div className="col-lg-4">
                          <label>Technology Icon (100 X 100 px):*</label>
                          <input
                            type="file"
                            className="form-control"
                            onChange={(e) =>
                              e.target.files[0] && handleTechIcon(ci, ti, e.target.files[0])
                            }
                          />
                          {preview.tech_stack_icon_previews?.[`${ci}-${ti}`] && (
                            <img
                              src={preview.tech_stack_icon_previews[`${ci}-${ti}`]}
                              alt={`tech-${ci}-${ti}`}
                              style={{ width: 60, marginTop: 6 }}
                            />
                          )}
                        </div>

                        <div className="col-lg-4">
                          <label>Alt Icon Text:*</label>
                          <input
                            className="form-control"
                            value={tech.alt_tech_stack_icon}
                            onChange={(e) =>
                              updateTechnology(ci, ti, "alt_tech_stack_icon", e.target.value)
                            }
                          />
                        </div>
                      </div>

                      {ti > 0 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger mt-2"
                          onClick={() => removeTechnology(ci, ti)}
                        >
                          Remove Technology
                        </button>
                      )}
                    </div>
                  ))}

                  <div style={{ textAlign: "center", marginTop: 15 }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-success"
                      onClick={() => addTechnology(ci)}
                    >
                      + Add Technology
                    </button>
                  </div>

                  {ci > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger mt-3"
                      onClick={() => removeTechCategory(ci)}
                    >
                      Remove Category
                    </button>
                  )}
                </div>
              ))}

              <div style={{ textAlign: "center", marginTop: 20 }}>
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  onClick={addTechCategory}
                >
                  + Add New Category
                </button>
              </div>
            </div>

            {/* Impact in numbers */}
            <div className="col-md-12 mt-4">
              <h4>Impact Number Section</h4>
              {postData.impact_in_numbers.map((impact, idx) => (
                <div key={idx} className="section-wrapper">
                  <div className="row">
                    <div className="col-lg-6">
                      <label>Number {idx + 1}:*</label>
                      <input className="form-control" value={impact.heading} onChange={(e) => updateImpact(idx, "heading", e.target.value)} />

                    </div>
                    <div className="col-lg-6">
                      <label>Description {idx + 1}:*</label>
                      <input className="form-control" value={impact.description} onChange={(e) => updateImpact(idx, "description", e.target.value)} />
                    </div>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    {/* remove only for non-first */}
                    {idx > 0 && <button type="button" className="btn btn-sm btn-danger" onClick={() => removeImpact(idx)}>Remove</button>}
                  </div>
                </div>
              ))}

              {/* SINGLE Add More button for the whole Impact section */}
              {postData?.impact_in_numbers?.length < 4 && <div style={{ marginBottom: 20, textAlign: "center" }}>
                <button type="button" className="btn btn-sm btn-success" onClick={addImpact}>+ Add More Impact</button>
              </div>}
            </div>

            {/* Submit */}
            <div className="col-md-12">
              <button type="submit" className="btn btn-primary submit-formbtn">Update Case Study</button>
            </div>
          </div>
        </form>
      </div>
    </>
  )
}
